import * as React from 'react';
import { LocationAccumulationSummary, UnderwritingTrendResult } from '../../../../types/reporting.type';
import { fetchLocationAccumulationsSummary, fetchUnderwritingTrendResult } from '../../../../services/reporting';
import { FunctionComponent, useContext, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { formatDollar, formatPercent } from '../../../../helpers/formatters';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import styles from './styles';
import { Spacing } from '../../../Layout';
import { withStyles, WithStyles } from '@mui/styles';
import { InstallationProviderContext } from '../../../../application/Providers/Installation';

interface Props {
    locationId: number;
    submissionId: string;
}

const UnderwritingTrendTable: FunctionComponent<Props & WithStyles<typeof styles>> = (props) => {
    const { locationId, submissionId, classes } = { ...props };
    const installationProvider = useContext(InstallationProviderContext);

    const [underwritingTrendData, setUnderwritingTrendData] = React.useState<UnderwritingTrendResult>(
        {} as UnderwritingTrendResult
    );
    const [expanded, setExpanded] = React.useState<boolean | string>(false);

    const fetchUnderwritingTrend = async () => {
        const trendResult = await fetchUnderwritingTrendResult(installationProvider, submissionId, locationId);
        setUnderwritingTrendData(trendResult);
    };

    useEffect(() => {
        if (submissionId) {
            fetchUnderwritingTrend();
        }
    }, [submissionId, locationId]);

    const buildUnderwritingTrendTable = (data: UnderwritingTrendResult) => (
        <>
            <Table size={'small'}>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell align={'right'}>0-1 mo.</TableCell>
                        <TableCell align={'right'}>1-3 mo.</TableCell>
                        <TableCell align={'right'}>3-6 mo.</TableCell>
                        <TableCell align={'right'}>6-12 mo.</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow key={data.groupName}>
                        <TableCell>{data.groupName}</TableCell>
                        <TableCell>
                            Avg. Rate
                            <br />
                            Avg. WHH Ded
                        </TableCell>
                        <TableCell align={'right'}>
                            {data.finalRate1Mo ? formatDollar(data.finalRate1Mo, 3) : 'N/A'}
                            <br />
                            {data.whhDeductible1Mo ? formatPercent(data.whhDeductible1Mo, 3) : 'N/A'}
                        </TableCell>
                        <TableCell align={'right'}>
                            {data.finalRate3Mo ? formatDollar(data.finalRate3Mo, 3) : 'N/A'}
                            <br />
                            {data.whhDeductible3Mo ? formatPercent(data.whhDeductible3Mo, 3) : 'N/A'}
                        </TableCell>
                        <TableCell align={'right'}>
                            {data.finalRate6Mo ? formatDollar(data.finalRate6Mo, 3) : 'N/A'}
                            <br />
                            {data.whhDeductible6Mo ? formatPercent(data.whhDeductible6Mo, 3) : 'N/A'}
                        </TableCell>
                        <TableCell align={'right'}>
                            {data.finalRate12Mo ? formatDollar(data.finalRate12Mo, 3) : 'N/A'}
                            <br />
                            {data.whhDeductible12Mo ? formatPercent(data.whhDeductible12Mo, 3) : 'N/A'}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            {!data && (
                <>
                    <Spacing height={'12px'} />
                    <Typography>
                        Currently there are no quoted or bound policies contributing to Trends for this location.
                    </Typography>
                </>
            )}
        </>
    );

    return <>{buildUnderwritingTrendTable(underwritingTrendData)}</>;
};

export default withStyles(styles)(UnderwritingTrendTable);
